import { Button, Col, Divider, Modal, Row, Space, Table } from 'antd';
import { useIntl } from 'react-intl';
import { useState } from 'react';
import { useDisclosure } from '@/hooks';
import { ColumnsType, DeleteIcon, EditIcon } from '@/components/_common';
import {
  InstallmentModalEnum,
  InstallmentMoreThanOneResponse,
  InstallmentOneResponse,
} from '@/core/domain/invoice/Installment/Installment.types';

import { useRouteMatch } from 'react-router-dom';
import {
  useDeleteInstallmentInvoice,
  useGetAllInstallmentInvoice,
} from '@/core/domain/invoice/Installment/Installment.hook';
import moment, { Moment } from 'moment';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import InvoiceInstallmentModal from './InvoiceInstallmentModal';

interface Props {
  disabledFields?: boolean;
}

export default function InvoiceInstallment({ disabledFields }: Props) {
  const intl = useIntl().formatMessage;
  const translate = (message: string, type: 'column' | 'button' = 'column') =>
    intl({ id: `invoicePaymentCondition.${type}.${message}` });

  const [visible, setVisible] = useDisclosure(false);
  const [installmentSelected, setIntallmentSelected] = useState<number>();
  const [modalType, setModalType] = useState<InstallmentModalEnum>(
    InstallmentModalEnum.one,
  );

  const { invoiceId } = useRouteMatch<{ invoiceId: string }>().params;

  const { data, isLoading } = useGetAllInstallmentInvoice(+invoiceId);
  const installments = flatInfiniteData(data);

  const { mutateAsync: deleteOneMutateAsync } = useDeleteInstallmentInvoice();

  const columns: ColumnsType<InstallmentMoreThanOneResponse> = [
    {
      title: translate('installmentNumber'),
      dataIndex: 'number',
      key: 'number',
    },
    {
      title: translate('expirationDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (value: Moment) =>
        value ? moment(value).format('DD/MM/YYYY') : '-',
    },
    {
      title: translate('installmentValue'),
      dataIndex: 'value',
      key: 'value',
    },
    {
      title: intl({
        id: 'insurers.contacts.list.header.actions',
      }),
      key: 'actions',
      dataIndex: 'actions',
      align: 'right',
      fixed: 'right',
      render: (_, record: InstallmentOneResponse) => (
        <Space>
          <EditIcon
            iconProps={{
              onClick: () => {
                setIntallmentSelected(record.id);
                setModalType(InstallmentModalEnum.one);
                setVisible.toggle();
              },
            }}
            disabled={disabledFields}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => {
              Modal.confirm({
                title: 'Deseja realmente excluir a parcela?',
                content:
                  'Ao remover a parcela, a recuperação do mesmo não poderá ser feita. Ainda sim deseja coninuar?',
                onOk: () => {
                  deleteOneMutateAsync({
                    invoiceId: Number(invoiceId),
                    id: Number(record.id),
                  });
                },
              });
            }}
            disabled={disabledFields}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[16, 16]} justify="end">
        <Col>
          <Button
            type="primary"
            onClick={() => {
              setVisible.toggle(), setModalType(InstallmentModalEnum.one);
            }}
            disabled={disabledFields}
          >
            {translate('newInstallment', 'button')}
          </Button>
        </Col>

        <Col>
          <Button
            type="primary"
            onClick={() => {
              setVisible.toggle(),
                setModalType(InstallmentModalEnum.moreThanOne);
            }}
            disabled={disabledFields}
          >
            {translate('multipleInstallments', 'button')}
          </Button>
        </Col>

        <Col span={24}>
          <Table<InstallmentMoreThanOneResponse>
            columns={columns}
            dataSource={installments}
            className="table-small"
            pagination={false}
            loading={isLoading}
          />
        </Col>
      </Row>

      <InvoiceInstallmentModal
        visible={visible}
        modalType={modalType}
        setModalType={() => setModalType}
        installmentSelected={installmentSelected}
        onClose={() => {
          setVisible.close();
          setIntallmentSelected(undefined);
        }}
      />
    </>
  );
}
